"use client";

import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import {
  formatCompactRelativeSlot,
  formatRelativeSlot,
} from "~/components/homepage/relative-slot";
import { cn } from "~/lib/utils";

/**
 * Renders a therapist's first bookable slot in the visitor's local timezone.
 * Formats only after mount: the server doesn't know the visitor's timezone,
 * so rendering the time during SSR would show the wrong local time.
 */
export function NextSlotLabel({
  iso,
  className,
  variant = "inline",
}: {
  iso: string | undefined;
  className?: string;
  variant?: "inline" | "compact";
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const date = iso ? new Date(iso) : null;
  // Cached timestamps can lapse; never show a slot that is already gone.
  const isUpcoming = mounted && date !== null && date.getTime() > Date.now();

  if (variant === "compact") {
    return (
      <p
        className={cn(
          "text-brand flex min-h-5 items-center gap-1.5 text-xs leading-4 font-medium",
          className,
        )}
      >
        {isUpcoming && date && (
          <>
            <CalendarDays aria-hidden="true" className="size-3.5 shrink-0" />
            <span>Termini od {formatCompactRelativeSlot(date)}</span>
          </>
        )}
      </p>
    );
  }

  return (
    <p
      className={cn(
        "text-muted-foreground flex min-h-5 items-center gap-1.5 text-sm",
        className,
      )}
    >
      {isUpcoming && date && (
        <>
          <span
            aria-hidden="true"
            className="inline-block size-1.5 shrink-0 rounded-full bg-green-500"
          />
          Slobodan termin {formatRelativeSlot(date)}
        </>
      )}
    </p>
  );
}
